import ChevronDownThin from '@assets/icons/chevron-down-thin.svg';
import i18n from '@/utils/i18n';
import { Analytics } from '@/utils/analytics';
import { createStyles } from '@/contexts/theme-context';
import { Pressable, type StyleProp, View, type ViewStyle } from 'react-native';
import { Typography } from '@usepepper/sprout-kit-ui';
import { useLayoutContext } from '@/contexts/layout-context';

export interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  disabled?: boolean;
  restaurantAddress?: string;
  restaurantName?: string;
  showLocationPicker?: () => void;
}

export const InboundDetailListCustomerAccountPicker = ({
  containerStyle,
  disabled,
  restaurantAddress,
  restaurantName,
  showLocationPicker,
}: Props) => {
  const { isSmallLayout } = useLayoutContext();

  const [styles, theme] = useStyles();

  const onCustomerAccountPickerClick = () => {
    if (disabled || !showLocationPicker) {
      return;
    }

    showLocationPicker();
    Analytics.track('order_inbound_detail.select_location', { restaurantName });
  };

  return (
    <Pressable
      disabled={disabled}
      onPress={onCustomerAccountPickerClick}
      style={[!isSmallLayout ? styles.containerWide : null, containerStyle]}
      testID="order-inbound-select-location"
    >
      <Typography bold marginBottom={8} size="$sm">
        {i18n.t('inbound_detail_list.customer_account_input_label')}
      </Typography>

      <View style={[!disabled ? styles.pointer : null, styles.field]}>
        <View style={styles.fieldText}>
          <Typography bold numberOfLines={1} size="$sm" variant="label">
            {restaurantName ?? ''}
          </Typography>

          {restaurantAddress ? (
            <Typography muted numberOfLines={1} size="$xs">
              {restaurantAddress}
            </Typography>
          ) : null}
        </View>

        {disabled ? null : (
          <View style={styles.icon}>
            <ChevronDownThin
              size={20}
              stroke={theme.sk.color.icon.primary}
              testID="order-inbound-select-location-chevron"
            />
          </View>
        )}
      </View>
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  containerWide: {
    flexDirection: 'column',
  },
  field: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderColor: theme.sk.color.border.input.default,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    flexDirection: 'row',
    gap: 8,
    minHeight: 40,
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
    paddingVertical: 5,
  },
  fieldText: {
    flex: 1,
  },
  icon: {
    alignItems: 'center',
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  pointer: {
    cursor: 'pointer',
  },
}));
